import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AppLayout } from '../layout/AppLayout';
import { RequireAdmin, RequireAnonymous, RequireAuth } from './guards';
import { LoginPage, RegisterPage } from '../../features/auth';
import {
  ArchiveScreen,
  TodayScreen,
  TomorrowScreen,
} from '../../features/tasks';
import { Spinner } from '../../shared/ui/Button';

const AdminScreen = lazy(async () => {
  const module = await import('../../features/admin');
  return { default: module.AdminScreen };
});

const SettingsScreen = lazy(async () => {
  const module = await import('../../features/settings');
  return { default: module.SettingsScreen };
});

const TagsManagerScreen = lazy(async () => {
  const module = await import('../../features/tags');
  return { default: module.TagsManagerScreen };
});

const ProjectsManagerScreen = lazy(async () => {
  const module = await import('../../features/projects');
  return { default: module.ProjectsManagerScreen };
});

const ProjectTasksScreen = lazy(async () => {
  const module = await import('../../features/projects');
  return { default: module.ProjectTasksScreen };
});

const CalendarScreen = lazy(async () => {
  const module = await import('../../features/tasks/ui/CalendarScreen');
  return { default: module.CalendarScreen };
});

const IdeasScreen = lazy(async () => {
  const module = await import('../../features/ideas');
  return { default: module.IdeasScreen };
});

const HabitsScreen = lazy(async () => {
  const module = await import('../../features/habits');
  return { default: module.HabitsScreen };
});

function RouteFallback() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <div className="flex items-center gap-3 text-tg-text-secondary">
        <Spinner size="lg" />
        <span>Загрузка…</span>
      </div>
    </div>
  );
}

export function AppRoutes() {
  return (
    <Routes>
      <Route element={<RequireAnonymous />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
      </Route>

      <Route element={<RequireAuth />}>
        <Route element={<AppLayout />}>
          <Route index element={<Navigate to="/today" replace />} />
          <Route path="/inbox" element={<Navigate to="/today" replace />} />
          <Route path="/today" element={<TodayScreen />} />
          <Route path="/tomorrow" element={<TomorrowScreen />} />
          <Route
            path="/calendar"
            element={
              <Suspense fallback={<RouteFallback />}>
                <CalendarScreen />
              </Suspense>
            }
          />
          <Route
            path="/ideas"
            element={
              <Suspense fallback={<RouteFallback />}>
                <IdeasScreen />
              </Suspense>
            }
          />
          <Route
            path="/habits"
            element={
              <Suspense fallback={<RouteFallback />}>
                <HabitsScreen />
              </Suspense>
            }
          />
          <Route path="/archive" element={<ArchiveScreen />} />
          <Route
            path="/settings"
            element={
              <Suspense fallback={<RouteFallback />}>
                <SettingsScreen />
              </Suspense>
            }
          />
          <Route
            path="/tags"
            element={
              <Suspense fallback={<RouteFallback />}>
                <TagsManagerScreen />
              </Suspense>
            }
          />
          <Route
            path="/projects"
            element={
              <Suspense fallback={<RouteFallback />}>
                <ProjectsManagerScreen />
              </Suspense>
            }
          />
          <Route
            path="/projects/:projectId"
            element={
              <Suspense fallback={<RouteFallback />}>
                <ProjectTasksScreen />
              </Suspense>
            }
          />
          <Route
            path="/admin"
            element={
              <RequireAdmin>
                <Suspense fallback={<RouteFallback />}>
                  <AdminScreen />
                </Suspense>
              </RequireAdmin>
            }
          />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/today" replace />} />
    </Routes>
  );
}
